import SelectSearch from '@/components/react-hook-form-inputs/select-search/select-search-component';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import Styled from '../configuration-style';
import InfoModal from './info-modal';

/**
 * DropdownComponent component to render a dropdown
 *
 * @param {object} props - The props object
 * @param {Array} props.options - The options to render
 * @param {Function} props.onSelect - The function to call when an option is selected
 * @param {Function} props.validateOption - The function to validate an option
 * @param {object} props.parentNode - The parent node data
 * @param {string} props.parentName - The name of the parent field
 * @param {string} props.name - The name of the current field
 * @param {string} props.label - The label of the dropdown
 * @param {number} props.level - The level of the dropdown
 * @returns {JSX.Element} - The dropdown component
 */
const DropdownComponent = ({
  options,
  onSelect,
  validateOption,
  parentNode,
  parentName,
  name,
  label,
  level,
}) => {
  const [infoModal, setInfoModal] = useState({ show: false, node: null });

  const { control, setValue } = useFormContext();

  const handleSelect = (e) => {
    onSelect(() => {
      setValue(`${name}.selectedOption`, e.value);
      options.forEach((option, index) => {
        if (option.id === e.value) {
          setValue(`${name}.children.${index}.id`, e.value);
        } else {
          setValue(`${name}.children.${index}.id`, null);
        }
      });
    }, null);
  };

  const handleDeselect = (value) => {
    setValue(`${name}.selectedOption`, null);
    options.forEach((option, index) => {
      setValue(`${name}.children.${index}.id`, null);
    });
    onSelect(null, value);
  };

  return (
    <Styled.DropdownContainer level={level * 20}>
      <SelectSearch
        cy="selDropdown"
        placeholder={`Please Select ${label}`}
        name=""
        options={options?.map((child) => ({
          value: child.id,
          label: child.name,
        }))}
        onChange={handleSelect}
        renderError={false}
      />

      <Controller
        name={`${name}.selectedOption`}
        control={control}
        rules={{
          validate: () =>
            validateOption({
              parentNode,
              parentName,
            }),
        }}
        render={({ field: { value } }) => {
          let selectedOptionIndex = null;

          if (!value) {
            return null;
          }
          const selectedOption = options.find((option, index) => {
            if (option.id === value) {
              selectedOptionIndex = index;
              return true;
            }
            return false;
          });

          return (
            <Styled.OptionContainer fullWidth $padding="0" $marginTop="8px">
              <Styled.OptionCardContainer
                selected
                id={`${selectedOption.id}_${selectedOption.part_number}`}
                data-name={`${name}.children${selectedOptionIndex}.id`}
                data-selectType="single"
                onClick={() => {
                  handleDeselect(value);
                }}
              >
                <Styled.OptionNameContainer>
                  <Styled.IconHolder>
                    <Styled.SelectedOptionHolder>
                      <Styled.RadioDisplay
                        $selected
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeselect(value);
                        }}
                      />
                      <Styled.SelectedOptionLabel>
                        {selectedOption.name}
                      </Styled.SelectedOptionLabel>
                    </Styled.SelectedOptionHolder>
                    <Styled.ActionHolder>
                      <Styled.InfoIcon
                        size={24}
                        relative
                        onClick={(e) => {
                          e.stopPropagation();
                          setInfoModal({
                            node: selectedOption.option_price_link_id,
                            show: true,
                          });
                        }}
                      />
                      <Styled.DeselectIcon
                        size={24}
                        relative
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeselect(value);
                        }}
                      />
                    </Styled.ActionHolder>
                  </Styled.IconHolder>
                </Styled.OptionNameContainer>
              </Styled.OptionCardContainer>
              <InfoModal
                showPrimaryButton={false}
                id={infoModal.node}
                open={infoModal.show}
                close={() => setInfoModal({ show: false, node: null })}
              />
            </Styled.OptionContainer>
          );
        }}
      />
    </Styled.DropdownContainer>
  );
};

DropdownComponent.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    }),
  ),
  onSelect: PropTypes.func,
  validateOption: PropTypes.func,
  parentNode: PropTypes.object,
  parentName: PropTypes.string,
  name: PropTypes.string,
  label: PropTypes.string,
  level: PropTypes.number,
};

DropdownComponent.defaultProps = {
  options: [],
  onSelect: () => {},
  validateOption: () => {},
  parentNode: {},
  parentName: '',
  name: '',
  label: '',
  level: 0,
};

export default DropdownComponent;
